import * as S from "./styles.ts";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import useDebouncedValue from "@/lib/helpers/hooks/useDebouncedValue.ts";
import { useTypedDispatch, useTypedSelector } from "@/redux/helpers/hooks.ts";
import { INumberQueryOptions } from "@/redux/slices/numbers/types.ts";
import {
  changeNumberCategory,
  changeNumberOption,
} from "@/redux/slices/numbers";
import { Form } from "@template/components";

const numberCategoryOptions = [
  {
    key: "all",
    label: "Standard & Memorable",
  },
  {
    key: "standard",
    label: "Standard Numbers Only",
  },
  {
    key: "premium",
    label: "Memorable Numbers Only",
  },
];

interface IAdvancedOptionsProps {
  mounted: boolean;
}

const minVal = (val: string, min: number) => {
  if (val === "") return "";
  const firstChar = val.charAt(0);
  return (
    (firstChar === "0" && val.length > 1 ? "0" : "") +
    Math.max(Number(val), min)
  );
};

const AdvancedOptions = ({ mounted }: IAdvancedOptionsProps) => {
  const dispatch = useTypedDispatch();

  const options = useTypedSelector((state) => state.numbers.options);

  const toggleOption = (key: keyof INumberQueryOptions, value: any) =>
    dispatch(changeNumberOption({ key, value }));

  const numberCategory = options.numberCategory;
  const setNumberCategory = (val: "all" | "standard" | "premium") =>
    dispatch(changeNumberCategory(val));

  const startsWith = options.startsWith;
  const setStartsWith = (val: string) => toggleOption("startsWith", val);

  const contains = options.contains;
  const setContains = (val: string) => toggleOption("contains", val);

  const endsWith = options.endsWith;
  const setEndsWith = (val: string) => toggleOption("endsWith", val);

  const [visibleStartsWith, setVisibleStartsWith] = useState(startsWith);
  const [visibleContains, setVisibleContains] = useState(contains);
  const [visibleEndsWith, setVisibleEndsWith] = useState(endsWith);

  const debouncedStartsWith = useDebouncedValue(visibleStartsWith, 500);
  const debouncedContains = useDebouncedValue(visibleContains, 500);
  const debouncedEndsWith = useDebouncedValue(visibleEndsWith, 500);

  useEffect(() => {
    setStartsWith(debouncedStartsWith);
  }, [debouncedStartsWith]);

  useEffect(() => {
    setContains(debouncedContains);
  }, [debouncedContains]);

  useEffect(() => {
    setEndsWith(debouncedEndsWith);
  }, [debouncedEndsWith]);

  return (
    <S.AdvancedOptionsWrapper
      as={motion.div}
      initial={
        mounted ? { opacity: 0, height: 0 } : { opacity: 1, height: "auto" }
      }
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
    >
      <S.AdvancedOptionsContainerWrapper>
        <S.AdvancedOptionsContainer>
          <Form
            simple
            value={{
              numberCategory,
              startsWith: visibleStartsWith,
              contains: visibleContains,
              endsWith: visibleEndsWith,
            }}
            onChange={(data) => {
              const keys = Object.keys(data) as (keyof INumberQueryOptions)[];
              const vals = Object.values(data);

              keys.forEach((key) => {
                switch (key) {
                  case "numberCategory":
                    setNumberCategory(vals[0] as any);
                    break;
                  case "startsWith":
                    setVisibleStartsWith(minVal(vals[1], 0) as any);
                    break;
                  case "contains":
                    setVisibleContains(minVal(vals[2], 0) as any);
                    break;
                  case "endsWith":
                    setVisibleEndsWith(minVal(vals[3], 0) as any);
                    break;
                }
              });
            }}
            inputs={[
              {
                label: "Number Category",
                type: "select",
                name: "numberCategory",
                options: numberCategoryOptions,
                search: true,
                span: 3,
              },
              {
                type: "number",
                label: "Starts With",
                name: "startsWith",
                placeholder: "e.g. 12",
                span: 3,
              },
              {
                type: "number",
                label: "Contains",
                name: "contains",
                placeholder: "e.g. 12",
                span: 3,
              },
              {
                type: "number",
                label: "Ends With",
                name: "endsWith",
                placeholder: "e.g. 12",
                span: 3,
              },
            ]}
          />
        </S.AdvancedOptionsContainer>
      </S.AdvancedOptionsContainerWrapper>
    </S.AdvancedOptionsWrapper>
  );
};

export default AdvancedOptions;
